import React, { useState } from "react"
import { cn } from "@/lib/utils"
import { ChapterList, Chapter } from "@/components/academician/chapter-list"
import { PainPointsTab, PainPoint } from "@/components/academician/pain-points-tab"
import { CustomerSegmentsTab, CustomerSegment } from "@/components/academician/customer-segments-tab"
import { ContactsTab, Contact } from "@/components/academician/contacts-tab"
import { InterviewsTab, Interview } from "@/components/academician/interviews-tab"
import { Button } from "@/components/ui/button"
import { MenuIcon, XIcon, ExternalLinkIcon, ClipboardListIcon } from "lucide-react"

interface ApiPaths {
  startup: string
  segments: string
  painPoints: string
  contacts: string
  interviews: string
}

export interface ActivityLearningPageProps {
  moduleTitle?: string
  activityTitle?: string
  activityDescription?: string
  activityType?: string
  csrfToken?: string
  startupPath?: string
  apiPaths?: ApiPaths
  painPoints?: PainPoint[]
  segments?: CustomerSegment[]
  contacts?: Contact[]
  interviews?: Interview[]
  chapters?: Chapter[]
  className?: string
}

async function apiCall(url: string, method: string, csrfToken: string, body?: Record<string, unknown>) {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      "Accept": "application/json"
    },
    body: body ? JSON.stringify(body) : undefined
  })
  if (!response.ok) {
    const data = await response.json().catch(() => null)
    const msg = data?.errors?.join(", ") || "Something went wrong"
    alert(msg)
    return null
  }
  return response
}

function painPointStatusToRails(status: string): string {
  switch (status) {
    case "testing": return "validating"
    case "validated": return "validated"
    case "invalidated": return "invalidated"
    default: return "hypothesis"
  }
}

function segmentStatusToRails(status: string): string {
  return status === "inactive" ? "pivoted" : "active"
}

function interviewTypeToRails(type: string): string {
  switch (type) {
    case "video-call": return "video_call"
    case "phone": return "phone_call"
    case "in-person": return "in_person"
    default: return "video_call"
  }
}

const ACTIVITY_LABELS: Record<string, string> = {
  customer_discovery: "Customer Discovery",
  pain_point_mapping: "Pain Points",
  contact_outreach: "Contacts",
  interview_session: "Interviews",
  data_submission: "Data Submission",
  general: "General Activity"
}

export function ActivityLearningPage({
  moduleTitle = "Module",
  activityTitle = "Activity",
  activityDescription,
  activityType = "general",
  csrfToken = "",
  startupPath = "/founder/startup",
  apiPaths,
  painPoints: initialPainPoints = [],
  segments: initialSegments = [],
  contacts: initialContacts = [],
  interviews: initialInterviews = [],
  chapters = [],
  className
}: ActivityLearningPageProps) {
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false)
  const [painPoints, setPainPoints] = useState(initialPainPoints)
  const [segments, setSegments] = useState(initialSegments)
  const [contacts, setContacts] = useState(initialContacts)
  const [interviews, setInterviews] = useState(initialInterviews)

  // --- Segment CRUD ---
  const handleAddSegment = async (segment: Omit<CustomerSegment, "id">) => {
    if (!apiPaths) return
    const resp = await apiCall(apiPaths.segments, "POST", csrfToken, {
      customer_segment: { name: segment.name, description: segment.description, status: segmentStatusToRails(segment.status) }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setSegments([...segments, data.segment])
  }

  const handleEditSegment = async (id: string, segment: Omit<CustomerSegment, "id">) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.segments}/${id}`, "PATCH", csrfToken, {
      customer_segment: { name: segment.name, description: segment.description, status: segmentStatusToRails(segment.status) }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setSegments(segments.map((s) => (s.id === id ? data.segment : s)))
  }

  const handleDeleteSegment = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.segments}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) setSegments(segments.filter((s) => s.id !== id))
  }

  // --- Pain Point CRUD ---
  const handleAddPainPoint = async (painPoint: Omit<PainPoint, "id">) => {
    if (!apiPaths) return
    const matchingSegment = segments.find(s => s.name === painPoint.segment)
    const resp = await apiCall(apiPaths.painPoints, "POST", csrfToken, {
      pain_point: { description: painPoint.painPoint, status: painPointStatusToRails(painPoint.status), customer_segment_id: matchingSegment?.id || null, severity: 3 }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setPainPoints([...painPoints, data.painPoint])
  }

  const handleEditPainPoint = async (id: string, painPoint: Omit<PainPoint, "id">) => {
    if (!apiPaths) return
    const matchingSegment = segments.find(s => s.name === painPoint.segment)
    const resp = await apiCall(`${apiPaths.painPoints}/${id}`, "PATCH", csrfToken, {
      pain_point: { description: painPoint.painPoint, status: painPointStatusToRails(painPoint.status), customer_segment_id: matchingSegment?.id || null }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setPainPoints(painPoints.map((p) => (p.id === id ? data.painPoint : p)))
  }

  const handleDeletePainPoint = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.painPoints}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) setPainPoints(painPoints.filter((p) => p.id !== id))
  }

  // --- Contact CRUD ---
  const handleAddContact = async (contact: Omit<Contact, "id">) => {
    if (!apiPaths) return
    const matchingSegment = segments.find(s => s.name === contact.segment)
    const resp = await apiCall(apiPaths.contacts, "POST", csrfToken, {
      contact: { name: contact.name, source: contact.source, status: contact.status || "to_contact", customer_segment_id: matchingSegment?.id || null }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setContacts([...contacts, data.contact])
  }

  const handleEditContact = async (id: string, contact: Omit<Contact, "id">) => {
    if (!apiPaths) return
    const matchingSegment = segments.find(s => s.name === contact.segment)
    const resp = await apiCall(`${apiPaths.contacts}/${id}`, "PATCH", csrfToken, {
      contact: { name: contact.name, source: contact.source, status: contact.status, customer_segment_id: matchingSegment?.id || null }
    })
    if (!resp) return
    const data = await resp.json()
    if (data.success) setContacts(contacts.map((c) => (c.id === id ? data.contact : c)))
  }

  const handleDeleteContact = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.contacts}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) setContacts(contacts.filter((c) => c.id !== id))
  }

  // --- Interview CRUD ---
  const buildInterviewFormData = (interview: Omit<Interview, "id">, method?: string, file?: File | null, removeRec?: boolean) => {
    const fd = new FormData()
    if (method) fd.append("_method", method)
    fd.append("interview[contact_name]", interview.contact)
    fd.append("interview[interview_date]", interview.date)
    fd.append("interview[interview_type]", interviewTypeToRails(interview.type))
    fd.append("interview[key_learnings]", interview.keyInsights)
    fd.append("interview[follow_up_notes]", interview.fullNotes)
    if (file) fd.append("interview[recording]", file)
    if (removeRec) fd.append("interview[remove_recording]", "1")
    return fd
  }

  const handleAddInterview = async (interview: Omit<Interview, "id">, file?: File | null) => {
    if (!apiPaths) return
    const fd = buildInterviewFormData(interview, undefined, file)
    fd.append("interview[status]", "completed")
    const response = await fetch(apiPaths.interviews, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      body: fd
    })
    if (!response.ok) {
      const data = await response.json().catch(() => null)
      alert(data?.errors?.join(", ") || "Something went wrong")
      return
    }
    const data = await response.json()
    if (data.success) setInterviews([...interviews, data.interview])
  }

  const handleEditInterview = async (id: string, interview: Omit<Interview, "id">, file?: File | null, removeRec?: boolean) => {
    if (!apiPaths) return
    const fd = buildInterviewFormData(interview, "PATCH", file, removeRec)
    const response = await fetch(`${apiPaths.interviews}/${id}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      body: fd
    })
    if (!response.ok) {
      const data = await response.json().catch(() => null)
      alert(data?.errors?.join(", ") || "Something went wrong")
      return
    }
    const data = await response.json()
    if (data.success) setInterviews(interviews.map((i) => (i.id === id ? data.interview : i)))
  }

  const handleDeleteInterview = async (id: string) => {
    if (!apiPaths) return
    const resp = await apiCall(`${apiPaths.interviews}/${id}`, "DELETE", csrfToken)
    if (!resp) return
    const data = await resp.json()
    if (data.success) setInterviews(interviews.filter((i) => i.id !== id))
  }

  const renderContent = () => {
    switch (activityType) {
      case "pain_point_mapping":
        return (
          <PainPointsTab
            painPoints={painPoints}
            onAdd={handleAddPainPoint}
            onEdit={handleEditPainPoint}
            onDelete={handleDeletePainPoint}
          />
        )
      case "contact_outreach":
        return (
          <ContactsTab
            contacts={contacts}
            onAdd={handleAddContact}
            onEdit={handleEditContact}
            onDelete={handleDeleteContact}
            onLogInterview={(contactId) => {
              window.location.href = `/founder/interviews/new?contact_id=${contactId}`
            }}
          />
        )
      case "interview_session":
        return (
          <InterviewsTab
            interviews={interviews}
            onAdd={handleAddInterview}
            onEdit={handleEditInterview}
            onDelete={handleDeleteInterview}
          />
        )
      case "customer_discovery":
        return (
          <CustomerSegmentsTab
            segments={segments}
            onAdd={handleAddSegment}
            onEdit={handleEditSegment}
            onDelete={handleDeleteSegment}
          />
        )
      default:
        return (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <ClipboardListIcon className="w-12 h-12 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">{activityTitle}</h3>
            {activityDescription && (
              <p className="text-sm text-muted-foreground max-w-md text-center">{activityDescription}</p>
            )}
            <Button asChild variant="outline">
              <a href={startupPath}>
                <ExternalLinkIcon className="w-4 h-4 mr-2" />
                Go to My Startup
              </a>
            </Button>
          </div>
        )
    }
  }

  return (
    <div className={cn("flex flex-col h-screen bg-background", className)}>
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-border bg-card">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
        >
          <MenuIcon className="w-5 h-5" />
        </Button>
        <h1 className="text-sm font-semibold text-foreground truncate">
          {moduleTitle}
        </h1>
        <div className="w-8" />
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Chapter List */}
        <aside
          className={cn(
            "w-[250px] border-r border-border bg-card overflow-y-auto",
            "lg:block",
            "fixed lg:relative inset-y-0 left-0 z-40 lg:z-0",
            "transition-transform duration-300",
            leftSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          )}
        >
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLeftSidebarOpen(false)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>

          <div className="hidden lg:flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
          </div>

          <ChapterList
            moduleTitle={moduleTitle || "Module"}
            chapters={chapters}
          />
        </aside>

        {/* Main Content - Validation CRUD */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto p-4 md:p-6 lg:p-8">
            {/* Activity Header */}
            <div className="mb-6 space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                  {ACTIVITY_LABELS[activityType] || "Activity"}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-foreground">{activityTitle}</h1>
              {activityDescription && (
                <p className="text-muted-foreground">{activityDescription}</p>
              )}
            </div>

            {/* CRUD Content */}
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {leftSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setLeftSidebarOpen(false)}
        />
      )}
    </div>
  )
}
